// Workspace-level Spaces management section.
// Features: list spaces; create (name/icon/color); rename/recolor/icon (PATCH);
// delete with confirm — handles 409 default-space and 409 has-projects via
// toasts; member management (list/add via user picker with role viewer|editor
// / remove) with last-editor 409 guard. Mutations gated by my_role === 'editor'.

import { useState } from "preact/hooks";
import { Eye, FolderOpen, Pencil, Plus, Trash2, Users } from "lucide-preact";
import type { Space, SpaceMemberRole } from "@juuplan/shared";
import { COLOR_PRESETS } from "../lib/format";
import { toastError, toastSuccess } from "../lib/toast";
import {
  useSpaces,
  createSpace,
  updateSpace,
  deleteSpace,
} from "../store/spaces";
import {
  listSpaceMembers,
  addSpaceMember,
  removeSpaceMember,
  type SpaceMemberItem,
} from "../lib/api";
import { useUsers } from "../store/users";
import { AccessOverviewDialog } from "./AccessOverviewDialog";
import { ApiError } from "../lib/api";
import {
  useFolders,
  addFolder,
  patchFolder,
  removeFolder,
} from "../store/folders";
import type { ApiFolder } from "@juuplan/shared";

import { Button } from "./ui/Button";

// ---- ICON presets (emoji shorthand) ----------------------------------------

const ICON_PRESETS = ["🏠", "🚀", "💼", "🎯", "🛠️", "📦", "🌐", "📊", "🔒", "⭐"];

// ---- sub-types ----------------------------------------------------------------

interface SpaceEdit {
  name: string;
  icon: string | null;
  color: string | null;
}

interface MembersState {
  loading: boolean;
  items: SpaceMemberItem[];
}

/**
 * Workspace-level Spaces management list.
 * Rendered inside Settings as an accordion-style section; mirrors the
 * StatusesSection pattern.
 */
export function SpacesSection(): preact.JSX.Element {
  const { items, loading, error } = useSpaces();

  // Inline edit state: spaceId -> draft
  const [editing, setEditing] = useState<Map<number, SpaceEdit>>(new Map());
  /** Space, dessen Zugriffs-Uebersicht offen ist (null = keine). */
  const [accessSpaceId, setAccessSpaceId] = useState<number | null>(null);
  // Rows pending a save
  const [saving, setSaving] = useState<Set<number>>(new Set());
  // Rows pending a delete confirm
  const [confirmDel, setConfirmDel] = useState<Set<number>>(new Set());

  // Members panel: spaceId -> members state (null = not open)
  const [membersOpen, setMembersOpen] = useState<Set<number>>(new Set());
  const [membersCache, setMembersCache] = useState<Map<number, MembersState>>(new Map());
  // Adding a member: spaceId -> {userId, role}
  const [addingMember, setAddingMember] = useState<Map<number, { userId: number | null; role: SpaceMemberRole }>>(new Map());
  const [memberSaving, setMemberSaving] = useState<Set<number>>(new Set());

  // Folders panel: spaceId -> open
  const [foldersOpen, setFoldersOpen] = useState<Set<number>>(new Set());
  // Folder inline rename: folderId -> draft name
  const [folderRenaming, setFolderRenaming] = useState<Map<number, string>>(new Map());
  // Folder rename saving: folderId
  const [folderRenameSaving, setFolderRenameSaving] = useState<Set<number>>(new Set());
  // Folder delete confirm: folderId
  const [folderConfirmDel, setFolderConfirmDel] = useState<Set<number>>(new Set());
  // Folder delete saving: folderId
  const [folderDelSaving, setFolderDelSaving] = useState<Set<number>>(new Set());
  // Add-folder form: spaceId -> draft name
  const [addFolderDraft, setAddFolderDraft] = useState<Map<number, string>>(new Map());
  // Add-folder saving: spaceId
  const [addFolderSaving, setAddFolderSaving] = useState<Set<number>>(new Set());

  // Create-new form
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState<string | null>(null);
  const [newColor, setNewColor] = useState<string>(COLOR_PRESETS[0]);
  const [creating, setCreating] = useState(false);

  const { items: allUsers, loading: usersLoading } = useUsers();

  // ---- edit helpers -----------------------------------------------------------

  const startEdit = (s: Space): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.set(s.id, { name: s.name, icon: s.icon, color: s.color });
      return next;
    });
  };

  const cancelEdit = (id: number): void => {
    setEditing((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const setDraft = (id: number, patch: Partial<SpaceEdit>): void => {
    setEditing((prev) => {
      const cur = prev.get(id);
      if (!cur) return prev;
      const next = new Map(prev);
      next.set(id, { ...cur, ...patch });
      return next;
    });
  };

  const commitEdit = async (s: Space): Promise<void> => {
    const draft = editing.get(s.id);
    if (!draft) return;
    const name = draft.name.trim() || s.name;
    setSaving((prev) => new Set(prev).add(s.id));
    try {
      await updateSpace(s.id, {
        name,
        icon: draft.icon,
        color: draft.color,
      });
      cancelEdit(s.id);
      toastSuccess("Space gespeichert.");
    } catch {
      // api wrapper already toasted
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  // ---- delete helpers ---------------------------------------------------------

  const handleDelete = async (s: Space): Promise<void> => {
    setSaving((prev) => new Set(prev).add(s.id));
    try {
      await deleteSpace(s.id);
      setConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
      toastSuccess("Space in den Papierkorb verschoben.");
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409 || err.status === 400) {
          const msg = typeof err.body === "object" && err.body && "error" in err.body
            ? String((err.body as { error: unknown }).error)
            : err.message;
          // Translate known server messages to German
          if (msg.includes("default") || msg.includes("general")) {
            toastError("Standard-Space kann nicht in den Papierkorb verschoben werden.");
          } else if (msg.includes("projects") || msg.includes("project")) {
            toastError("Space enthält Projekte — bitte zuerst die Projekte verschieben oder löschen.");
          } else {
            toastError(msg || "Space kann nicht in den Papierkorb verschoben werden.");
          }
        }
        // 4xx errors already toasted by the api wrapper for mutations
      }
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  // ---- members helpers --------------------------------------------------------

  const loadMembers = async (spaceId: number): Promise<void> => {
    setMembersCache((prev) => {
      const next = new Map(prev);
      next.set(spaceId, { loading: true, items: [] });
      return next;
    });
    try {
      const items = await listSpaceMembers(spaceId);
      setMembersCache((prev) => {
        const next = new Map(prev);
        next.set(spaceId, { loading: false, items });
        return next;
      });
    } catch {
      setMembersCache((prev) => {
        const next = new Map(prev);
        next.set(spaceId, { loading: false, items: [] });
        return next;
      });
      // Sonst zeigt der aufgeklappte Space "keine Mitglieder" statt eines Fehlers.
      toastError("Mitglieder konnten nicht geladen werden.");
    }
  };

  const toggleMembers = (spaceId: number): void => {
    setMembersOpen((prev) => {
      const next = new Set(prev);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
        void loadMembers(spaceId);
      }
      return next;
    });
  };

  const handleAddMember = async (spaceId: number): Promise<void> => {
    const state = addingMember.get(spaceId);
    if (!state?.userId) return;
    setMemberSaving((prev) => new Set(prev).add(spaceId));
    try {
      await addSpaceMember(spaceId, state.userId, state.role);
      setAddingMember((prev) => {
        const next = new Map(prev);
        next.delete(spaceId);
        return next;
      });
      toastSuccess("Mitglied hinzugefügt.");
      await loadMembers(spaceId);
    } catch {
      // api wrapper already toasted
    } finally {
      setMemberSaving((prev) => {
        const next = new Set(prev);
        next.delete(spaceId);
        return next;
      });
    }
  };

  const handleRemoveMember = async (spaceId: number, userId: number): Promise<void> => {
    setMemberSaving((prev) => new Set(prev).add(spaceId));
    try {
      await removeSpaceMember(spaceId, userId);
      toastSuccess("Mitglied entfernt.");
      await loadMembers(spaceId);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        toastError("Letzten Editor kann nicht entfernt werden. Zuerst einen anderen Editor ernennen.");
      }
      // other errors already toasted by api wrapper
    } finally {
      setMemberSaving((prev) => {
        const next = new Set(prev);
        next.delete(spaceId);
        return next;
      });
    }
  };

  // ---- folders helpers --------------------------------------------------------

  const toggleFolders = (spaceId: number): void => {
    setFoldersOpen((prev) => {
      const next = new Set(prev);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
      }
      return next;
    });
  };

  const startFolderRename = (f: ApiFolder): void => {
    setFolderRenaming((prev) => {
      const next = new Map(prev);
      next.set(f.id, f.name);
      return next;
    });
    setFolderConfirmDel((prev) => {
      const next = new Set(prev);
      next.delete(f.id);
      return next;
    });
  };

  const cancelFolderRename = (folderId: number): void => {
    setFolderRenaming((prev) => {
      const next = new Map(prev);
      next.delete(folderId);
      return next;
    });
  };

  const commitFolderRename = async (f: ApiFolder): Promise<void> => {
    const name = (folderRenaming.get(f.id) ?? "").trim();
    if (!name) return;
    setFolderRenameSaving((prev) => new Set(prev).add(f.id));
    try {
      await patchFolder(f.id, f.space_id, { name });
      cancelFolderRename(f.id);
      toastSuccess("Ordner umbenannt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setFolderRenameSaving((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
    }
  };

  const handleDeleteFolder = async (f: ApiFolder): Promise<void> => {
    setFolderDelSaving((prev) => new Set(prev).add(f.id));
    try {
      await removeFolder(f.id, f.space_id);
      setFolderConfirmDel((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
      toastSuccess("Ordner gelöscht.");
    } catch {
      // api wrapper already toasted
    } finally {
      setFolderDelSaving((prev) => {
        const next = new Set(prev);
        next.delete(f.id);
        return next;
      });
    }
  };

  const submitAddFolder = async (spaceId: number): Promise<void> => {
    const name = (addFolderDraft.get(spaceId) ?? "").trim();
    if (!name) return;
    setAddFolderSaving((prev) => new Set(prev).add(spaceId));
    try {
      await addFolder(spaceId, { name });
      setAddFolderDraft((prev) => {
        const next = new Map(prev);
        next.delete(spaceId);
        return next;
      });
      toastSuccess("Ordner hinzugefügt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setAddFolderSaving((prev) => {
        const next = new Set(prev);
        next.delete(spaceId);
        return next;
      });
    }
  };

  // ---- create helper ----------------------------------------------------------

  const submitNew = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await createSpace({ name, icon: newIcon, color: newColor });
      setNewName("");
      setNewIcon(null);
      setNewColor(COLOR_PRESETS[0]);
      setShowNew(false);
      toastSuccess("Space angelegt.");
    } catch {
      // api wrapper already toasted
    } finally {
      setCreating(false);
    }
  };

  // ---- render -----------------------------------------------------------------

  return (
    <div class="flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Spaces
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {error && (
        <p class="text-xs text-[color:var(--color-error)]">Fehler: {error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Noch keine Spaces.
        </p>
      )}

      {!loading && items.map((s) => {
        const draft = editing.get(s.id);
        const isSaving = saving.has(s.id);
        const isConfirmDel = confirmDel.has(s.id);
        const canEdit = s.my_role === "editor";
        const isMembersOpen = membersOpen.has(s.id);
        const membersState = membersCache.get(s.id);
        const isMemberSaving = memberSaving.has(s.id);
        const addState = addingMember.get(s.id);
        const isFoldersOpen = foldersOpen.has(s.id);
        const isAddFolderSaving = addFolderSaving.has(s.id);
        const addFolderName = addFolderDraft.get(s.id) ?? "";

        return (
          <div
            key={s.id}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden"
          >
            {/* Row header */}
            <div class="flex items-center gap-2 px-2 py-1.5">
              {/* Color swatch + icon */}
              <span
                class="inline-flex items-center justify-center size-5 rounded shrink-0 text-xs"
                style={{ background: draft ? (draft.color ?? "#737686") : (s.color ?? "#737686") }}
                aria-hidden="true"
              >
                {draft ? (draft.icon ?? "") : (s.icon ?? "")}
              </span>

              {/* Name */}
              <span class="flex-1 text-xs font-medium text-[color:var(--color-on-surface)] truncate">
                {draft ? draft.name || s.name : s.name}
              </span>

              {/* Role badge */}
              {s.my_role && (
                <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                  {s.my_role === "editor" ? "Editor" : "Betrachter"}
                </span>
              )}

              {/* Members toggle */}
              <button
                type="button"
                onClick={() => toggleMembers(s.id)}
                class={`rounded p-0.5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] ${
                  isMembersOpen
                    ? "text-[color:var(--color-primary)] bg-[color:var(--color-primary-container)]/30"
                    : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                }`}
                aria-label="Mitglieder"
                title="Mitglieder verwalten"
              >
                <Users size={14} aria-hidden="true" />
              </button>

              {/* Zugriffs-Uebersicht. Die Mitglieder-Verwaltung daneben zeigt
                  nur direkte space_members — wer ueber eine Abteilungs-Freigabe
                  oder die Gesamtorganisation hereinkommt, steht dort nicht,
                  obwohl er denselben Zugriff hat. Dieser Knopf zeigt beides
                  plus die Folge daraus (welche Projekte und Listen damit
                  erreichbar werden). Fuer JEDEN mit Space-Zugriff. */}
              <button
                type="button"
                onClick={() => setAccessSpaceId(s.id)}
                class="rounded p-0.5 text-[color:var(--color-on-surface-variant)] transition-colors hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                aria-label="Wer hat Zugriff?"
                title="Wer hat Zugriff auf diesen Space?"
              >
                <Eye size={14} aria-hidden="true" />
              </button>

              {/* Folders toggle */}
              <button
                type="button"
                onClick={() => toggleFolders(s.id)}
                class={`rounded p-0.5 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] ${
                  isFoldersOpen
                    ? "text-[color:var(--color-primary)] bg-[color:var(--color-primary-container)]/30"
                    : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                }`}
                aria-label="Ordner"
                title="Ordner verwalten"
              >
                <FolderOpen size={14} aria-hidden="true" />
              </button>

              {/* Edit + delete — editor only */}
              {canEdit && !draft && (
                <div class="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(s)}
                    class="rounded px-1.5 py-0.5 text-label text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.add(s.id);
                        return next;
                      })
                    }
                    disabled={isSaving}
                    class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                    aria-label="Space in den Papierkorb verschieben"
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            {/* Inline editor */}
            {draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 pb-2 pt-2 flex flex-col gap-2">
                <input
                  type="text"
                  value={draft.name}
                  maxLength={100}
                  onInput={(e) => setDraft(s.id, { name: (e.target as HTMLInputElement).value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void commitEdit(s); }
                    if (e.key === "Escape") cancelEdit(s.id);
                  }}
                  placeholder="Name"
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Name"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                {/* Icon picker */}
                <div class="flex flex-wrap gap-1" role="group" aria-label="Icon wählen">
                  <button
                    type="button"
                    onClick={() => setDraft(s.id, { icon: null })}
                    class={`size-6 rounded text-xs border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                      draft.icon === null
                        ? "border-[color:var(--color-on-surface)] scale-110"
                        : "border-transparent hover:scale-110 hover:bg-[color:var(--color-surface-container-low)]"
                    }`}
                    aria-label="Kein Icon"
                    aria-pressed={draft.icon === null}
                  >
                    —
                  </button>
                  {ICON_PRESETS.map((ic) => (
                    <button
                      key={ic}
                      type="button"
                      onClick={() => setDraft(s.id, { icon: ic })}
                      class={`size-6 rounded text-xs border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                        draft.icon === ic
                          ? "border-[color:var(--color-on-surface)] scale-110"
                          : "border-transparent hover:scale-110"
                      }`}
                      aria-label={`Icon ${ic}`}
                      aria-pressed={draft.icon === ic}
                    >
                      {ic}
                    </button>
                  ))}
                </div>
                {/* Color picker */}
                <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setDraft(s.id, { color: c })}
                      class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                        draft.color === c
                          ? "border-[color:var(--color-on-surface)] scale-110"
                          : "border-transparent hover:scale-110"
                      }`}
                      style={{ background: c }}
                      aria-label={`Farbe ${c}`}
                      aria-pressed={draft.color === c}
                    />
                  ))}
                </div>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() => cancelEdit(s.id)}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="primary"
                    size="xs"
                    type="button"
                    onClick={() => void commitEdit(s)}
                    disabled={isSaving || !draft.name.trim()}
                  >
                    {isSaving ? "Speichere…" : "Speichern"}
                  </Button>
                </div>
              </div>
            )}

            {/* Delete confirm */}
            {isConfirmDel && !draft && canEdit && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-1.5">
                <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                  Space „{s.name}" in den Papierkorb verschieben?
                </p>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmDel((prev) => {
                        const next = new Set(prev);
                        next.delete(s.id);
                        return next;
                      })
                    }
                    class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void handleDelete(s)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Verschiebe…" : "Verschieben"}
                  </Button>
                </div>
              </div>
            )}

            {/* Members panel */}
            {isMembersOpen && (
              <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-2">
                <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
                  Mitglieder
                </div>

                {membersState?.loading && (
                  <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
                )}

                {membersState && !membersState.loading && membersState.items.length === 0 && (
                  <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
                    Keine direkten Mitglieder.
                  </p>
                )}

                {membersState && !membersState.loading && membersState.items.map((m) => (
                  <div key={m.user_id} class="flex items-center gap-2">
                    <span class="flex-1 text-xs text-[color:var(--color-on-surface)] truncate">
                      {m.name || m.email}
                    </span>
                    <span class="shrink-0 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-1.5 py-0.5">
                      {m.role === "editor" ? "Editor" : "Betrachter"}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        disabled={isMemberSaving}
                        onClick={() => void handleRemoveMember(s.id, m.user_id)}
                        class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                        aria-label={`${m.name || m.email} entfernen`}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Add member — editor only */}
                {canEdit && (
                  <div class="flex flex-col gap-1.5 pt-1 border-t border-[color:var(--color-outline-variant)]">
                    <div class="text-label text-[color:var(--color-on-surface-variant)] font-medium">
                      Mitglied hinzufügen
                    </div>
                    <div class="flex items-center gap-1.5">
                      <select
                        value={addState?.userId ?? ""}
                        onChange={(e) => {
                          const val = (e.target as HTMLSelectElement).value;
                          const uid = val ? parseInt(val, 10) : null;
                          setAddingMember((prev) => {
                            const next = new Map(prev);
                            next.set(s.id, { userId: uid, role: addState?.role ?? "viewer" });
                            return next;
                          });
                        }}
                        class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-1.5 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                        aria-label="Benutzer auswählen"
                      >
                        <option value="">Benutzer wählen…</option>
                        {!usersLoading && allUsers.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name || u.email}
                          </option>
                        ))}
                      </select>
                      <select
                        value={addState?.role ?? "viewer"}
                        onChange={(e) => {
                          const role = (e.target as HTMLSelectElement).value as SpaceMemberRole;
                          setAddingMember((prev) => {
                            const next = new Map(prev);
                            next.set(s.id, { userId: addState?.userId ?? null, role });
                            return next;
                          });
                        }}
                        class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-1.5 py-1 text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                        aria-label="Rolle"
                      >
                        <option value="viewer">Betrachter</option>
                        <option value="editor">Editor</option>
                      </select>
                      <Button
                        variant="primary"
                        size="xs"
                        type="button"
                        disabled={!addState?.userId || isMemberSaving}
                        onClick={() => void handleAddMember(s.id)}
                      >
                        {isMemberSaving ? "…" : "Hinzufügen"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Folders panel */}
            {isFoldersOpen && (
              <FoldersPanel
                spaceId={s.id}
                canEdit={canEdit}
                folderRenaming={folderRenaming}
                folderRenameSaving={folderRenameSaving}
                folderConfirmDel={folderConfirmDel}
                folderDelSaving={folderDelSaving}
                addFolderName={addFolderName}
                isAddFolderSaving={isAddFolderSaving}
                onStartRename={startFolderRename}
                onCancelRename={cancelFolderRename}
                onSetRenameDraft={(folderId, name) =>
                  setFolderRenaming((prev) => {
                    const next = new Map(prev);
                    next.set(folderId, name);
                    return next;
                  })
                }
                onCommitRename={commitFolderRename}
                onConfirmDel={(folderId) =>
                  setFolderConfirmDel((prev) => new Set(prev).add(folderId))
                }
                onCancelDel={(folderId) =>
                  setFolderConfirmDel((prev) => {
                    const next = new Set(prev);
                    next.delete(folderId);
                    return next;
                  })
                }
                onDeleteFolder={handleDeleteFolder}
                onSetAddFolderDraft={(name) =>
                  setAddFolderDraft((prev) => {
                    const next = new Map(prev);
                    next.set(s.id, name);
                    return next;
                  })
                }
                onSubmitAddFolder={() => void submitAddFolder(s.id)}
              />
            )}
          </div>
        );
      })}

      {/* Add new space */}
      <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
        {!showNew ? (
          <button
            type="button"
            onClick={() => setShowNew(true)}
            class="w-full inline-flex items-center gap-1.5 px-2 py-1.5 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] transition-colors"
          >
            <Plus size={14} aria-hidden="true" />
            Neuen Space anlegen
          </button>
        ) : (
          <div class="px-2 py-2 flex flex-col gap-2">
            <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
              Neuer Space
            </div>
            <input
              type="text"
              value={newName}
              maxLength={100}
              onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void submitNew(); }
                if (e.key === "Escape") { setShowNew(false); setNewName(""); }
              }}
              placeholder="Name des Spaces"
              class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
              aria-label="Name des neuen Spaces"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {/* Icon picker */}
            <div class="flex flex-wrap gap-1" role="group" aria-label="Icon wählen">
              <button
                type="button"
                onClick={() => setNewIcon(null)}
                class={`size-6 rounded text-xs border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                  newIcon === null
                    ? "border-[color:var(--color-on-surface)] scale-110"
                    : "border-transparent hover:scale-110 hover:bg-[color:var(--color-surface-container-low)]"
                }`}
                aria-label="Kein Icon"
                aria-pressed={newIcon === null}
              >
                —
              </button>
              {ICON_PRESETS.map((ic) => (
                <button
                  key={ic}
                  type="button"
                  onClick={() => setNewIcon(ic)}
                  class={`size-6 rounded text-xs border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                    newIcon === ic
                      ? "border-[color:var(--color-on-surface)] scale-110"
                      : "border-transparent hover:scale-110"
                  }`}
                  aria-label={`Icon ${ic}`}
                  aria-pressed={newIcon === ic}
                >
                  {ic}
                </button>
              ))}
            </div>
            {/* Color picker */}
            <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  class={`size-5 rounded-full border-2 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
                    newColor === c
                      ? "border-[color:var(--color-on-surface)] scale-110"
                      : "border-transparent hover:scale-110"
                  }`}
                  style={{ background: c }}
                  aria-label={`Farbe ${c}`}
                  aria-pressed={newColor === c}
                />
              ))}
            </div>
            <div class="flex items-center gap-1.5 justify-end">
              <button
                type="button"
                onClick={() => { setShowNew(false); setNewName(""); setNewIcon(null); }}
                class="rounded border border-[color:var(--color-outline-variant)] px-2.5 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
              >
                Abbrechen
              </button>
              <Button
                variant="primary"
                size="xs"
                type="button"
                onClick={() => void submitNew()}
                disabled={!newName.trim() || creating}
              >
                {creating ? "Lege an…" : "Erstellen"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {accessSpaceId !== null && (
        <AccessOverviewDialog
          subject={{ kind: "space", id: accessSpaceId }}
          onClose={() => setAccessSpaceId(null)}
        />
      )}
    </div>
  );
}

// ---- FoldersPanel sub-component -----------------------------------------------

interface FoldersPanelProps {
  spaceId: number;
  canEdit: boolean;
  folderRenaming: Map<number, string>;
  folderRenameSaving: Set<number>;
  folderConfirmDel: Set<number>;
  folderDelSaving: Set<number>;
  addFolderName: string;
  isAddFolderSaving: boolean;
  onStartRename: (f: ApiFolder) => void;
  onCancelRename: (folderId: number) => void;
  onSetRenameDraft: (folderId: number, name: string) => void;
  onCommitRename: (f: ApiFolder) => Promise<void>;
  onConfirmDel: (folderId: number) => void;
  onCancelDel: (folderId: number) => void;
  onDeleteFolder: (f: ApiFolder) => Promise<void>;
  onSetAddFolderDraft: (name: string) => void;
  onSubmitAddFolder: () => void;
}

/**
 * Folders sub-panel rendered inside the space card accordion.
 * Uses useFolders(spaceId) to keep its own live subscription;
 * mutations are delegated back to SpacesSection via callbacks so all
 * saving/confirm state lives in one place.
 */
function FoldersPanel({
  spaceId,
  canEdit,
  folderRenaming,
  folderRenameSaving,
  folderConfirmDel,
  folderDelSaving,
  addFolderName,
  isAddFolderSaving,
  onStartRename,
  onCancelRename,
  onSetRenameDraft,
  onCommitRename,
  onConfirmDel,
  onCancelDel,
  onDeleteFolder,
  onSetAddFolderDraft,
  onSubmitAddFolder,
}: FoldersPanelProps): preact.JSX.Element {
  const { items, loading } = useFolders(spaceId);

  return (
    <div class="border-t border-[color:var(--color-outline-variant)] px-2 py-2 flex flex-col gap-2">
      <div class="text-label font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        Ordner
      </div>

      {loading && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
      )}

      {!loading && items.length === 0 && (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Keine Ordner.
        </p>
      )}

      {!loading && items.map((f) => {
        const renameDraft = folderRenaming.get(f.id);
        const isRenaming = renameDraft !== undefined;
        const isRenameSaving = folderRenameSaving.has(f.id);
        const isConfirmingDel = folderConfirmDel.has(f.id);
        const isDelSaving = folderDelSaving.has(f.id);

        return (
          <div key={f.id} class="flex flex-col gap-1">
            {/* Folder row */}
            {!isRenaming && !isConfirmingDel && (
              <div class="flex items-center gap-2">
                <span class="flex-1 text-xs text-[color:var(--color-on-surface)] truncate">
                  {f.name}
                </span>
                {canEdit && (
                  <div class="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => onStartRename(f)}
                      class="rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)]"
                      aria-label={`Ordner „${f.name}" umbenennen`}
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onConfirmDel(f.id)}
                      disabled={isDelSaving}
                      class="rounded p-0.5 text-[color:var(--color-error)] hover:bg-[color:var(--color-error-container)] disabled:opacity-30 focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-error)]"
                      aria-label={`Ordner „${f.name}" löschen`}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Inline rename */}
            {isRenaming && (
              <div class="flex items-center gap-1.5">
                <input
                  type="text"
                  value={renameDraft}
                  maxLength={100}
                  onInput={(e) => onSetRenameDraft(f.id, (e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); void onCommitRename(f); }
                    if (e.key === "Escape") onCancelRename(f.id);
                  }}
                  placeholder="Ordnername"
                  class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                  aria-label="Ordner umbenennen"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => onCancelRename(f.id)}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                >
                  Abbrechen
                </button>
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={() => void onCommitRename(f)}
                  disabled={isRenameSaving || !(renameDraft ?? "").trim()}
                >
                  {isRenameSaving ? "…" : "Umbenennen"}
                </Button>
              </div>
            )}

            {/* Delete confirm */}
            {isConfirmingDel && !isRenaming && (
              <div class="flex flex-col gap-1">
                <p class="text-label text-[color:var(--color-on-error-container)] bg-[color:var(--color-error-container)] rounded px-2 py-1.5">
                  Ordner „{f.name}" wirklich löschen?
                </p>
                <div class="flex items-center gap-1.5 justify-end">
                  <button
                    type="button"
                    onClick={() => onCancelDel(f.id)}
                    class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                  >
                    Abbrechen
                  </button>
                  <Button
                    variant="destructive"
                    size="xs"
                    type="button"
                    onClick={() => void onDeleteFolder(f)}
                    disabled={isDelSaving}
                  >
                    {isDelSaving ? "Lösche…" : "Löschen"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Add folder — editor only */}
      {canEdit && (
        <div class="flex items-center gap-1.5 pt-1 border-t border-[color:var(--color-outline-variant)]">
          <input
            type="text"
            value={addFolderName}
            maxLength={100}
            onInput={(e) => onSetAddFolderDraft((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); onSubmitAddFolder(); }
            }}
            placeholder="Neuer Ordner…"
            class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
            aria-label="Ordner hinzufügen"
          />
          <Button
            variant="primary"
            size="xs"
            type="button"
            onClick={onSubmitAddFolder}
            disabled={!addFolderName.trim() || isAddFolderSaving}
          >
            {isAddFolderSaving ? "…" : "Hinzufügen"}
          </Button>
        </div>
      )}
    </div>
  );
}
